import React from 'react';
import useReveal from '../../hooks/useReveal';

const SLAs = () => {
  document.title = 'Service Level Agreements - Cloudvantage';
  const [headingRef, headingVisible] = useReveal();
  return (
    <div className="max-w-4xl mx-auto py-10 px-4 md:px-6 text-black dark:text-white">
      <h1 ref={headingRef} className={`text-3l font-bold mb-6 ${headingVisible ? 'animate-reveal' : 'opacity-0'}`}>
        Service Level Agreements (SLAs)
      </h1>

      <section className="mb-8">
        <p className="mb-4">
          This Cloudvantage Limited, Inc. (“Company”) Service Level Agreement
          (“SLA”), as updated from time to time by Company in its sole
          discretion, supplements and its term and conditions are subject to
          Company’s{' '}
          <a
            href="/cloud-services-agreement"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Cloud Services Agreement
          </a>{' '}
          (“CSA”), entered into with Customers and Company pursuant to the
          Order, and subject to the{' '}
          <a
            href="/terms-of-use"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            Universal Terms of Use
          </a>{' '}
          (“UToU”) which are both incorporated herein by this reference.
          Capitalized terms used in this{' '}
          <a
            href="/cloud-services-agreement"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            CSA
          </a>{' '}
          but not defined herein shall have the meanings set forth in the Order,
          CSA, this SLA, and{' '}
          <a
            href="/terms-of-use"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            UToU
          </a>{' '}
          as applicable. In the event of a conflict between the terms of the
          Order,{' '}
          <a
            href="/cloud-services-agreement"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            CSA
          </a>
          , this SLA,{' '}
          <a
            href="/terms-of-use"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            UToU
          </a>
          , and/or any other agreement(s) governing the Cloud Services, the
          terms and conditions of this SLA shall apply in the following: the
          Order,{' '}
          <a
            href="/cloud-services-agreement"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            CSA
          </a>
          , this SLA,{' '}
          <a
            href="/terms-of-use"
            target="_blank"
            className="text-blue-600 hover:text-blue-800 no-underline"
          >
            UToU
          </a>
          , then any other agreements governing the Cloud Services, but only to
          the extent of such conflict.
        </p>

        <ol className="list-decimal pl-6 mt-4 mb-6">
          <li>
            <strong>Server Service Levels:</strong> Customer acknowledges,
            understands and agrees that this SLA only applies to both physical
            server and virtual server. Physical server is a bare-metal/computer
            server with the motherboard, CPU, memory, hard drive, and network
            connection (“Physical Server”) provided by the Company to the
            Customer. Virtual server/machine is a software-based environment
            that emulates all the functions of a physical server; used to deal
            with the virtualization of resources of a physical server
            provisioned by Customer within the physical server, which virtual
            server/machine is the absolute and sole responsibility of Customer.
          </li>
          <li>
            <strong>Service Levels and Service Credits</strong>
            <ol className="list-decimal pl-6 mt-4 mb-6">
              <li>
                The following chart sets forth the Cloud Services related to the
                Physical Server service levels to which this SLA applies, the
                applicable performance standard that Company seeks to meet or
                exceed in connection with the Cloud Services (the “Service
                Level”), and the percentage of amounts Customer will be eligible
                to receive a credit if Company fails to meet a Service Level
                (the “Service Credit”) based on a yearly uptime calculations as
                determined by Company. Upon written notice to Company by
                Customer, Company agrees to promptly interact with Customer to
                determine the cause of any failure to meet a Service Level and
                determine methods of preventing it from happening again.
                Customer agrees to cooperate with Company in all reasonable
                respects in connection to any such inquiry, including providing
                access to Customer’s computer systems, if and as reasonably
                required under the circumstances.
              </li>
              <section className="mb-8 mt-2">
                <div className="overflow-x-auto">
                  <table className="min-w-full border border-gray-200">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-4 py-2 text-left border-b border-gray-200">
                          Monthly Uptime Percentage
                        </th>
                        <th className="px-4 py-2 text-left border-b border-gray-200">
                          Service Credit Percentage
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="px-4 py-2 border-b border-gray-200">
                          Less than 99.99% but equal to or greater than 99.0%
                        </td>
                        <td className="px-4 py-2 border-b border-gray-200">
                          10%
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b border-gray-200">
                          Less than 99.0% but equal to or greater than 95.0%
                        </td>
                        <td className="px-4 py-2 border-b border-gray-200">
                          20%
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 border-b border-gray-200">
                          Less than 95.0%
                        </td>
                        <td className="px-4 py-2 border-b border-gray-200">
                          100%
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
              <li>
                Service Credits shall be provided to Customer on the invoice
                following the time period in which the Service Level failure
                occurred. If no additional amounts are due and owing by Company,
                however, the Service Credits shall be refunded to Customer
                within thirty (30) days of the incident to which such Service
                Credit(s) relate.
              </li>
              <li>
                Notwithstanding anything set forth herein or in the{' '}
                <a
                  href="/cloud-services-agreement"
                  target="_blank"
                  className="text-blue-600 hover:text-blue-800 no-underline"
                >
                  CSA
                </a>{' '}
                to the contrary, the Service Credits set forth herein are
                Customer’s sole and exclusive remedy in connection with any
                failure to meet Service Levels. The parties hereto acknowledge
                and agree that the Service Credits do not, in any way,
                constitute a penalty and/or liquidated damages.
              </li>
              <li>
                NOTWITHSTANDING ANYTHING TO THE CONTRARY IN THE AGREEMENTS,
                CUSTOMER SPECIFICALLY ACKNOWLEDGES AND AGREES COMPANY’S MAXIMUM
                LIABILITY UNDER THIS REFERRAL AGREEMENT FOR ANY DAMAGES, LOSSES,
                COSTS AND CAUSES OF ACTIONS FROM ANY AND ALL CLAIMS (WHETHER IN
                CONTRACT, TORT, INCLUDING NEGLIGENCE, QUASI-CONTRACT, STATUTORY
                OR OTHERWISE) SHALL NOT EXCEED THE ACTUAL DOLLAR AMOUNT PAID BY
                CUSTOMER FOR THE CLOUD SERVICES DURING THE 12-MONTH PERIOD PRIOR
                TO THE DATE THE DAMAGE OR LOSS OCCURRED OR THE CAUSE OF ACTION
                AROSE, OR $5,000 U.S. DOLLARS, WHICHEVER IS LESS.
              </li>
            </ol>
            <li>
              <strong>
                Service Credits, Service Credit Requests and Payment Procedures
              </strong>
              <ol className="list-decimal pl-6 mt-4 mb-6">
                <li>
                  Service Credits are calculated as a percentage of Customer’s
                  yearly bill total.
                </li>
                <li>
                  Any Service Credit will apply only against future payments for
                  the Cloud Services otherwise due by Customer. At Company’s
                  sole discretion, Company may issue a Service Credit to the
                  payment method in the Account in which the unavailability
                  occurred. Service Credits will not be issued as a refund or
                  other payment from. A Service Credit will be applicable and
                  issued only if the credit amount for the applicable yearly
                  billing cycle total is greater than one United States dollar
                  and 00/100 cents ($1.00 USD). Service Credits may not be
                  transferred or applied to any other account.
                </li>
                <li>
                  Service Credit Request
                  <ol className="list-decimal pl-6 mt-4 mb-6">
                    <li>
                      To receive a Service Credit, Customer must submit a
                      written claim to the Company. By submitting a request
                      Customer agrees to the terms of the{' '}
                      <a
                        href="/privacy-policy"
                        target="_blank"
                        className="text-blue-600 hover:text-blue-800 no-underline"
                      >
                        Privacy Policy
                      </a>{' '}
                      regarding Customer’s personally identifiable information.
                      The Service Credit request must be received by Company by
                      the end of the second billing cycle after which the
                      incident occurred.
                    </li>
                    <li>
                      Customer’s failure to provide the requested and other
                      information as required above will disqualify Customer
                      from receiving a Service Credit. Unless otherwise provided
                      in the Agreements, this SLA sets forth Customer’s sole and
                      exclusive remedies, and Company’s sole and exclusive
                      obligations, for any unavailability, non-performance, or
                      other failure by Company to provide the Cloud Services.
                    </li>
                  </ol>
                </li>
              </ol>
            </li>
            <li>
              <strong>SLA Exclusions:</strong> Notwithstanding anything set
              forth herein to the contrary, Customer shall not receive a Service
              Credit for any failure to meet a Service Level to the extent
              caused by any unavailability, suspension or termination of the
              Cloud Services, or any other Cloud Services performance issues,
              directly or indirectly: (i) caused by Customer’s (or any of its
              employees’ or agents’) failure to comply with all policies,
              procedures, instructions, and requirements for the applicable
              Cloud Service; (ii) any failure of power, facilities, equipment,
              systems, or connections not provided by Company; (iii) caused by
              factors outside of Company’s reasonable control, including any
              force majeure event, Internet access or related problems beyond
              the demarcation point of the Cloud Services; (iv) that result from
              any actions or inactions of Customer, including failure to
              acknowledge a recovery volume or respond to resource health
              concerns; (v) that result from Customer’s equipment, software or
              other technology (not provided by Company under the Order); (vi)
              bandwidth saturation or other resource exhaustion or outage caused
              by malicious traffic such as viruses, worms, trojan horses, denial
              of service (DOS) attacks, etc., not introduced by Company; (vii)
              service suspension for non-payment of any amounts due by Customer;
              (viii) arising from our suspension or termination of Customer’s
              right to use the applicable Cloud Services in accordance with the
              Agreements; or (ix) any material breach of the{' '}
              <a
                href="/cloud-services-agreement"
                target="_blank"
                className="text-blue-600 hover:text-blue-800 no-underline"
              >
                CSA
              </a>{' '}
              by Customer. If availability is impacted by factors other than
              those used in our Monthly Uptime Percentage calculation, then
              Company may issue a Service Credit considering such factors as
              determined in Company’s sole discretion.
            </li>
          </li>
        </ol>
      </section>
    </div>
  );
};

export default SLAs;
